import type { DeploymentOutcome, DeploymentStage } from '../../api';

/**
 * Stage grouping (UX_ARCHITECTURE.md, rule E). A long run is read for what
 * needs attention: the stage that failed, is running or warned stays a row
 * of its own; consecutive stages that succeeded, were skipped or did not run
 * fold into one line that opens to every one of them. Nothing is removed:
 * a folded stage is one click away, in its original order.
 */
export type StageRunKind = 'attention' | 'succeeded' | 'skipped' | 'not_run' | 'waiting';

export interface StageRun {
  kind: StageRunKind;
  stages: DeploymentStage[];
  /** 1-based position of the first stage in the whole run. */
  firstNumber: number;
}

function kindOf(stage: DeploymentStage): StageRunKind {
  switch (stage.status) {
    case 'failed':
    case 'running':
    case 'partial':
    case 'warning':
      return 'attention';
    case 'succeeded':
      return 'succeeded';
    case 'skipped':
      return 'skipped';
    case 'not_run':
    case 'cancelled':
      return 'not_run';
    default:
      return 'waiting';
  }
}

/** Walks the stages in order and folds neighbours of the same quiet kind. */
export function groupStages(stages: DeploymentStage[]): StageRun[] {
  const runs: StageRun[] = [];
  stages.forEach((stage, index) => {
    const kind = kindOf(stage);
    const last = runs[runs.length - 1];
    if (kind !== 'attention' && last && last.kind === kind) {
      last.stages.push(stage);
      return;
    }
    runs.push({ kind, stages: [stage], firstNumber: index + 1 });
  });
  return runs;
}

/** Whether a run is shown as its own row (attention) or folded into a line. */
export function isFolded(run: StageRun): boolean {
  return run.kind !== 'attention';
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

function took(stages: DeploymentStage[]): number {
  let total = 0;
  for (const stage of stages) {
    if (stage.started_at && stage.completed_at) total += Math.max(0, Date.parse(stage.completed_at) - Date.parse(stage.started_at));
  }
  return total;
}

/** The sum of the recorded durations of a run's stages, in milliseconds (0 when none was recorded). */
export function runDuration(run: StageRun): number {
  return took(run.stages);
}

/** The folded line's words, from what really happened to the stages. */
export function runLabel(run: StageRun, outcome: DeploymentOutcome): string {
  const count = run.stages.length;
  switch (run.kind) {
    case 'succeeded':
      return count === 1 ? '1 stage succeeded' : `${count} stages succeeded`;
    case 'skipped':
      return `${plural(count, 'stage')} skipped`;
    case 'not_run':
      return outcome === 'cancelled' ? `${plural(count, 'stage')} not run after the cancellation` : `${plural(count, 'stage')} not run after the failure`;
    case 'waiting':
      return `${plural(count, 'stage')} waiting`;
    default:
      return plural(count, 'stage');
  }
}

/**
 * True when the run folds by default. A run that holds the step a summary
 * pointed at opens, and so does a run inside a deployment that has not
 * finished and still has nothing to hide.
 */
export function runOpensFor(run: StageRun, targetKey: string | null | undefined): boolean {
  if (!targetKey) return false;
  return run.stages.some((stage) => stage.step_keys.includes(targetKey));
}
